import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { ROUTES, SITE, PUBLIC_ORIGIN, ALLOW_INDEXING, orgJsonLd } from "@/seo";

/**
 * Per-route SEO tags.
 *
 * Robots meta is host-driven at runtime:
 *   - Production (gpfzllc.com / www.gpfzllc.com): "index, follow, max-image-preview:large"
 *   - Preview / anything else: "noindex, nofollow"
 *   - /admin/* and /404: always "noindex, nofollow" regardless of env
 *
 * Canonicals and Open Graph URLs always resolve to the production origin
 * (https://gpfzllc.com) when the visitor is on production, so the canonical
 * URL is stable across www / apex landings. On preview they mirror the
 * current preview origin.
 */
export default function Seo({ overrideKey, jsonLd }) {
  const { pathname } = useLocation();
  const key = overrideKey || pathname;
  const entry = ROUTES[key] || ROUTES["/"];
  const isAdmin = pathname.startsWith("/admin");
  const is404 = key === "/404";
  const shouldIndex = ALLOW_INDEXING && !isAdmin && !is404;
  const robots = shouldIndex
    ? "index, follow, max-image-preview:large"
    : "noindex, nofollow";

  const base = PUBLIC_ORIGIN;
  const path = is404 ? pathname : key;
  const canonical = base ? `${base}${path}` : undefined;
  const ogImage = entry.ogImage && base ? `${base}${entry.ogImage}` : undefined;

  return (
    <Helmet prioritizeSeoTags>
      <title>{entry.title}</title>
      <meta name="description" content={entry.description} />
      <meta name="robots" content={robots} />
      {canonical && <link rel="canonical" href={canonical} />}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={entry.title} />
      <meta property="og:description" content={entry.description} />
      {canonical && <meta property="og:url" content={canonical} />}
      {ogImage && <meta property="og:image" content={ogImage} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={entry.title} />
      <meta name="twitter:description" content={entry.description} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}

export function OrgJsonLd() {
  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(orgJsonLd(PUBLIC_ORIGIN))}
      </script>
    </Helmet>
  );
}
